import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, message, Modal, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import NewButton from '../../../components/NewButton/NewButton';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import {
  getAllConfigurationUploads,
   deleteConfigurationUpload,
  getAllClientEnvironments,
} from '../../../infrastructure/apiClient';
import type {
  ClientEnvironmentDTO,
  ConfigurationUploadDTO,
} from '../../../infrastructure/models';
import CardTable from '../../../components/Table/CardTable';
import { buildFilterOptions, getCheckboxFilterProps } from '../../../components/Table/columnFilterUtils';
import DashboardLayout, { dashboardStyles as styles } from '../../../components/DashboardLayout/DashboardLayout';
import { getNextPageAfterDelete } from '../../../utils/pagination';
import { useDashboardData } from '../../../hooks/useDashboardData';
import { useDeleteConfirmation } from '../../../hooks/useDeleteConfirmation';
import { sortData, sortConfig, type SortConfig } from '../../../utils/sortUtils';
import { buildEnvironmentNameMap, getEnvironmentName } from '../../../utils/environmentUtils';
import ConfigurationUpload, { type ConfigurationUploadPayload } from '../ConfigurationUpload/ConfigurationUpload';

interface ConfigurationUploadDashboardProps {
  onComparisonSelect?: (upload: ConfigurationUploadDTO) => void;
  onNavigateToComparisonDashboard?: () => void;
}

const SORT_CONFIGS: SortConfig<ConfigurationUploadDTO>[] = [
  sortConfig('name', 'string'),
  sortConfig('description', 'string'),
  sortConfig('environmentId', 'string'),
  sortConfig('originalFileName', 'string'),
  sortConfig('fileSize', 'number'),
  sortConfig('createdBy', 'string'),
  sortConfig('createdAt', 'date'),
];

const ConfigurationUploadDashboard = ({ onComparisonSelect, onNavigateToComparisonDashboard }: ConfigurationUploadDashboardProps) => {
  const {
    data: comparisons,
    loading,
    setLoading,
    error,
    activeClient,
    tenantsLoading,
    pagination,
    sorter,
    tablePagination,
    handleTableChange,
    updateCurrentPage,
    fetchData: fetchComparisons,
    abortControllerRef,
    disableActions,
  } = useDashboardData<ConfigurationUploadDTO>({
    fetchFn: getAllConfigurationUploads,
    entityName: 'configuration uploads',
  });
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [environments, setEnvironments] = useState<ClientEnvironmentDTO[]>([]);
  const [environmentsLoading, setEnvironmentsLoading] = useState(false);

  const [nameFilters, setNameFilters] = useState<string[]>([]);
  const [descriptionFilters, setDescriptionFilters] = useState<string[]>([]);
  const [environmentFilters, setEnvironmentFilters] = useState<string[]>([]);
  const [fileNameFilters, setFileNameFilters] = useState<string[]>([]);
  const [expiredFilters, setExpiredFilters] = useState<string[]>([]);
  const [createdByFilters, setCreatedByFilters] = useState<string[]>([]);

  const environmentMap = useMemo(() => buildEnvironmentNameMap(environments), [environments]);

  useEffect(() => {
    if (!activeClient?.id) {
      setEnvironments([]);
      return;
    }
    const controller = new AbortController();
    setEnvironmentsLoading(true);
    getAllClientEnvironments(activeClient.id, { offset: 0, limit: 1000, signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) {
          setEnvironments(response?.data ?? []);
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          console.error('Failed to load environments', err);
          setEnvironments([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setEnvironmentsLoading(false);
        }
      });
    return () => controller.abort();
  }, [activeClient?.id]);

const handleDeleteUpload = useCallback(
  async (upload: ConfigurationUploadDTO) => {
    if (!activeClient?.id) {
      message.warning('Select a tenant before deleting an upload.');
      return;
    }
    try {
      setLoading(true);
      console.log('upload.id', upload.id);
      await deleteConfigurationUpload(activeClient.id, upload.id);

      const { nextPage, offset } = getNextPageAfterDelete({
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        totalItems: pagination.total,
        deletedItems: 1,
      });

      updateCurrentPage(nextPage);
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      await fetchComparisons(activeClient.id, {
        offset,
        limit: pagination.pageSize,
        signal: controller.signal,
      });

      message.success(`Upload "${upload.name}" deleted.`);
    } catch (err) {
      console.error('Failed to delete upload', err);
      message.error('Failed to delete upload.');
    } finally {
      setLoading(false);
    }
  },
  [activeClient?.id, fetchComparisons, pagination.current, pagination.pageSize, pagination.total, updateCurrentPage],
);

  const {
    confirmOpen,
    confirmLoading,
    itemToDelete: confirmComparison,
    requestDelete: handleOpenDeleteConfirm,
    cancelDelete: handleCloseDeleteConfirm,
    confirmDelete: handleConfirmDelete,
  } = useDeleteConfirmation<ConfigurationUploadDTO>(handleDeleteUpload);

  const nameOptions = useMemo(() => buildFilterOptions(comparisons, (r) => r.name ?? undefined), [comparisons]);
  const descriptionOptions = useMemo(() => buildFilterOptions(comparisons, (r) => r.description ?? undefined), [comparisons]);
  const environmentOptions = useMemo(() => buildFilterOptions(comparisons, (r) => getEnvironmentName(r.environmentId, environmentMap) || undefined), [comparisons, environmentMap]);
  const fileNameOptions = useMemo(() => buildFilterOptions(comparisons, (r) => r.originalFileName ?? undefined), [comparisons]);
  const expiredOptions = useMemo(() => buildFilterOptions(comparisons, (r) => r.isExpired ? 'Yes' : 'No'), [comparisons]);
  const createdByOptions = useMemo(() => buildFilterOptions(comparisons, (r) => r.createdBy ?? undefined), [comparisons]);

  const columns = useMemo<ColumnsType<ConfigurationUploadDTO>>(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        sorter: (a, b) => a.name.localeCompare(b.name),
        sortOrder: sorter.columnKey === 'name' ? sorter.order : null,
        ...getCheckboxFilterProps(nameFilters, setNameFilters, nameOptions, (r) => r.name ?? undefined),
        render: (value: string, record) => {
          if (!onComparisonSelect) {
            return value;
          }
          return (
            <button
              type="button"
              className={styles.nameLink}
              onClick={() => onComparisonSelect(record)}
            >
              {value}
            </button>
          );
        },
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        ellipsis: true,
        sorter: (a, b) => (a.description ?? '').localeCompare(b.description ?? ''),
        sortOrder: sorter.columnKey === 'description' ? sorter.order : null,
        ...getCheckboxFilterProps(descriptionFilters, setDescriptionFilters, descriptionOptions, (r) => r.description ?? undefined),
        render: (value: string | null) => value || '—',
      },
      {
        title: 'Environment',
        dataIndex: 'environmentId',
        key: 'environmentId',
        ellipsis: true,
        sorter: (a, b) => getEnvironmentName(a.environmentId, environmentMap).localeCompare(getEnvironmentName(b.environmentId, environmentMap)),
        sortOrder: sorter.columnKey === 'environmentId' ? sorter.order : null,
        ...getCheckboxFilterProps(environmentFilters, setEnvironmentFilters, environmentOptions, (r) => getEnvironmentName(r.environmentId, environmentMap) || undefined),
        render: (value: string) => getEnvironmentName(value, environmentMap),
      },
      {
        title: 'File Name',
        dataIndex: 'originalFileName',
        key: 'originalFileName',
        ellipsis: true,
        sorter: (a, b) => a.originalFileName.localeCompare(b.originalFileName),
        sortOrder: sorter.columnKey === 'originalFileName' ? sorter.order : null,
        ...getCheckboxFilterProps(fileNameFilters, setFileNameFilters, fileNameOptions, (r) => r.originalFileName ?? undefined),
      },
      {
        title: 'File Size',
        dataIndex: 'fileSize',
        key: 'fileSize',
        sorter: (a, b) => a.fileSize - b.fileSize,
        sortOrder: sorter.columnKey === 'fileSize' ? sorter.order : null,
        render: (value: number) => {
          if (value < 1024) return `${value} B`;
          if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
          return `${(value / (1024 * 1024)).toFixed(1)} MB`;
        },
      },
     
      {
        title: 'Created By',
        dataIndex: 'createdBy',
        key: 'createdBy',
        ellipsis: true,
        sorter: (a, b) => (a.createdBy ?? '').localeCompare(b.createdBy ?? ''),
        sortOrder: sorter.columnKey === 'createdBy' ? sorter.order : null,
        ...getCheckboxFilterProps(createdByFilters, setCreatedByFilters, createdByOptions, (r) => r.createdBy ?? undefined),
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (value: string) => dayjs(value).format('MMM D, YYYY h:mm A'),
        width: 200,
        sorter: (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
        sortOrder: sorter.columnKey === 'createdAt' ? sorter.order : null,
      },
      {
        title: 'Delete',
        key: 'delete',
        align: 'center',
        width: 80,
        render: (_value, record) => (
          <button
            type="button"
            className={styles.deleteButton}
            aria-label="Delete comparison"
            onClick={() => handleOpenDeleteConfirm(record)}
            disabled={loading || tenantsLoading || !activeClient?.id}
          >
            <DeleteOutlined />
          </button>
        ),
      },
    ],
    [
      sorter.columnKey,
      sorter.order,
      onComparisonSelect,
      handleOpenDeleteConfirm,
      loading,
      tenantsLoading,
      activeClient?.id,
      environmentMap,
      nameFilters, nameOptions, descriptionFilters, descriptionOptions,
      environmentFilters, environmentOptions, fileNameFilters, fileNameOptions,
      expiredFilters, expiredOptions, createdByFilters, createdByOptions,
    ],
  );

  const sortedComparisons = useMemo(
    () => sortData(comparisons, sorter, SORT_CONFIGS),
    [comparisons, sorter],
  );

  return (
    <DashboardLayout
      title="Configuration Comparison"
      actions={
        <div className="flex gap-2">
          <NewButton
            onClick={() => setUploadModalOpen(true)}
            disabled={disableActions}
            label="New Configuration Upload"
          />
          {onNavigateToComparisonDashboard && (
            <Button onClick={onNavigateToComparisonDashboard} disabled={disableActions}>
              Configuration Comparison Dashboard
            </Button>
          )}
        </div>
      }
    >
      <CardTable<ConfigurationUploadDTO>
        title="Configuration Uploads"
        columns={columns}
        dataSource={sortedComparisons}
        rowKey={(record) => record.id}
        loading={loading || tenantsLoading}
        pagination={tablePagination}
        onChange={handleTableChange}
        error={error}
        emptyText={
          disableActions
            ? 'Select a tenant to view configuration uploads.'
            : 'No configuration uploads found yet.'
        }
        className={styles.cardTable}
      />

      <Modal
        open={uploadModalOpen}
        title="New Configuration Upload"
        onCancel={() => setUploadModalOpen(false)}
        footer={null}
        destroyOnHidden
        width={640}
        styles={{ body: { minHeight: 360 } }}
      >
        <ConfigurationUpload
          environments={environments}
          environmentsLoading={environmentsLoading}
          onSubmit={async (payload: ConfigurationUploadPayload) => {
            if (!activeClient?.id) {
              message.warning('Select a tenant before uploading.');
              return;
            }
            try {
              setLoading(true);
              // TODO: call API to create configuration upload with payload
              abortControllerRef.current?.abort();
              const controller = new AbortController();
              abortControllerRef.current = controller;
              await fetchComparisons(activeClient.id, {
                offset: 0,
                limit: pagination.pageSize,
                signal: controller.signal,
              });
              message.success(`Upload "${payload.uploadName}" created.`);
              setUploadModalOpen(false);
            } catch (err) {
              console.error('Failed to create configuration upload', err);
              message.error('Failed to create configuration upload.');
            } finally {
              setLoading(false);
            }
          }}
        />
      </Modal>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete comparison"
        message={
          confirmComparison
            ? `Are you sure you want to delete the comparison "${confirmComparison.name}"?`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmLoading={confirmLoading}
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDeleteConfirm}
      />
    </DashboardLayout>
  );
};

export default ConfigurationUploadDashboard;
